/**
 * Microphone capture for the voice interview.
 *
 * Produces exactly what ElevenLabs Scribe wants — mono signed 16-bit PCM at the
 * server-chosen sample rate — and nothing else. The resampling is done by the
 * browser: an `AudioContext` constructed with an explicit `sampleRate`
 * resamples the hardware's native rate (usually 48 kHz) for us, which is both
 * faster and better than anything we'd hand-roll.
 *
 * ## Why 16 kHz isn't a compromise here
 *
 * It looks like one, but a Bluetooth hands-free profile (HFP) mic — which is
 * what a car kit or headset gives you the moment a page opens the microphone —
 * tops out at 8 or 16 kHz anyway. Asking for 48 kHz would upsample nothing into
 * more bytes over a phone's uplink.
 *
 * ## The Bluetooth trade nobody can avoid
 *
 * Acquiring a mic while a Bluetooth device is connected forces the OS to switch
 * that device from A2DP (stereo, high bitrate, output only) to HFP (mono,
 * narrowband, bidirectional). Music-grade playback and microphone input cannot
 * coexist on one Bluetooth device — that's the profile spec, not a browser
 * limitation. So the assistant's voice will sound like a phone call. That is
 * the correct trade for a hands-free interview, but it's why the audio is
 * noticeably worse than the same page on a laptop.
 */

/** One chunk of captured audio, ready to send. */
export type PcmChunkHandler = (chunk: Int16Array) => void;

/**
 * Samples buffered before a chunk is emitted. At 16 kHz, 1024 samples is 64ms —
 * short enough that server-side VAD stays responsive to the end of a sentence,
 * long enough that we aren't paying WebSocket framing overhead on every 8ms of
 * a drive.
 */
const CHUNK_SAMPLES = 1024;

/**
 * The worklet is defined as a source string and loaded from a Blob URL rather
 * than a separate file. It's ten lines that must stay in lockstep with
 * `CHUNK_SAMPLES` above, and keeping it here means no extra build-config entry
 * and no chance of the bundler and the worklet disagreeing about a path.
 *
 * Float32 → Int16 conversion happens inside the worklet, on the audio thread:
 * it halves the bytes crossing the message port, and the clamp is what stops a
 * loud moment (a horn, a door) from wrapping around into a burst of noise
 * instead of clipping.
 */
const WORKLET_SOURCE = `
class PcmCaptureProcessor extends AudioWorkletProcessor {
  constructor() {
    super();
    this._buffer = new Int16Array(${CHUNK_SAMPLES});
    this._offset = 0;
  }
  process(inputs) {
    const channel = inputs[0] && inputs[0][0];
    // No input yet (or the graph is being torn down) — stay alive, emit nothing.
    if (!channel) return true;
    for (let i = 0; i < channel.length; i++) {
      const sample = Math.max(-1, Math.min(1, channel[i]));
      // Asymmetric scaling: int16 holds -32768..32767, so -1 and +1 both map
      // in range instead of +1 overflowing to -32768.
      this._buffer[this._offset++] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
      if (this._offset === this._buffer.length) {
        // Copy, don't transfer: the buffer is reused for the next chunk.
        this.port.postMessage(this._buffer.slice(0));
        this._offset = 0;
      }
    }
    return true;
  }
}
registerProcessor('pcm-capture', PcmCaptureProcessor);
`;

export class MicCaptureError extends Error {}

/**
 * An open microphone streaming PCM chunks to a handler.
 *
 * Construct with {@link MicCapture.start}; the instance owns the media stream,
 * the audio graph, and the Blob URL, and releases all three on {@link stop}.
 */
export class MicCapture {
  private constructor(
    private readonly stream: MediaStream,
    private readonly context: AudioContext,
    private readonly node: AudioWorkletNode,
    private readonly source: MediaStreamAudioSourceNode,
  ) {}

  static async start(
    sampleRate: number,
    onChunk: PcmChunkHandler,
  ): Promise<MicCapture> {
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new MicCaptureError(
        "This browser can't access the microphone. Voice needs a secure (https) connection.",
      );
    }

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          // All three are the browser's own DSP, and all three matter more in a
          // car than at a desk: engine and road noise, cabin echo, and a talker
          // whose distance from the mic changes every time they check a mirror.
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
    } catch (err) {
      // NotAllowedError is a denied permission prompt; everything else is a
      // device that isn't there or is held by another app. Both are actionable
      // by the user, and neither is worth a stack trace.
      const denied = err instanceof DOMException && err.name === "NotAllowedError";
      throw new MicCaptureError(
        denied
          ? "Microphone access was blocked. Allow it in your browser settings to use voice."
          : "Couldn't open the microphone. Check that nothing else is using it.",
      );
    }

    let context: AudioContext;
    try {
      // The explicit sampleRate is the resampler: everything downstream of here
      // is already at Scribe's rate.
      context = new AudioContext({ sampleRate });
    } catch {
      stream.getTracks().forEach((t) => t.stop());
      throw new MicCaptureError("This browser can't record at the required audio quality.");
    }

    const workletUrl = URL.createObjectURL(
      new Blob([WORKLET_SOURCE], { type: "application/javascript" }),
    );
    try {
      await context.audioWorklet.addModule(workletUrl);
    } catch {
      stream.getTracks().forEach((t) => t.stop());
      void context.close();
      throw new MicCaptureError("This browser is too old for hands-free voice. Try Safari 15+ or Chrome.");
    } finally {
      // The module is compiled by now; the URL has done its job either way.
      URL.revokeObjectURL(workletUrl);
    }

    const node = new AudioWorkletNode(context, "pcm-capture");
    node.port.onmessage = (event) => onChunk(event.data as Int16Array);
    const source = context.createMediaStreamSource(stream);
    source.connect(node);
    // Deliberately NOT connected to context.destination: routing the mic to the
    // speakers would put the driver's own voice back into the car, and — with
    // the assistant playing through the same speakers — close a feedback loop.
    // Chrome keeps a worklet with no downstream connection running; the
    // zero-gain sink some guides recommend is unnecessary and audible on
    // hardware that doesn't honour a zero gain exactly.

    // Autoplay policy can hand back a suspended context even inside a click
    // handler on iOS. Resuming is cheap and a no-op when it's already running.
    if (context.state === "suspended") await context.resume();

    return new MicCapture(stream, context, node, source);
  }

  /**
   * Mute or unmute the microphone.
   *
   * Disabling the track makes it emit digital silence rather than tearing it
   * down, which is what keeps this usable as a half-duplex switch: the audio
   * graph and the Bluetooth HFP route stay up (re-acquiring them mid-interview
   * costs a second and an audible Bluetooth re-negotiation), the worklet keeps
   * producing chunks so the transcription socket never looks idle, and the
   * chunks contain nothing for the server's VAD to trigger on.
   */
  setMuted(muted: boolean): void {
    this.stream.getAudioTracks().forEach((track) => {
      track.enabled = !muted;
    });
  }

  /** True while the mic is live. Reflects the tracks, not a cached flag. */
  get live(): boolean {
    return this.stream.getAudioTracks().some((t) => t.readyState === "live");
  }

  /** Release the microphone, the audio graph, and the Bluetooth HFP route. */
  stop(): void {
    this.node.port.onmessage = null;
    this.source.disconnect();
    this.node.disconnect();
    this.stream.getTracks().forEach((track) => track.stop());
    // Closing the context is what actually drops the OS audio session — and so
    // what lets a Bluetooth device fall back to A2DP and sound normal again.
    void this.context.close();
  }
}
